// src/features/system/auth/RequireAuth.tsx
import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/shared/auth';
import Login from '@/features/system/auth/Login';
import { getAuthKickoutState } from '@/shared/auth/authKickout';
import { subscribeAuthDisconnected } from '@/lib/auth/authDisconnect';
import { isVisitorUser } from '@/shared/auth/isVisitorUser';

export default function RequireAuth() {
  const location = useLocation();
  const { user, loading } = useAuth();

  const hadUserRef = React.useRef(false);
  React.useEffect(() => {
    if (user) hadUserRef.current = true;
  }, [user]);

  const [kickout, setKickout] = React.useState(() => getAuthKickoutState());
  React.useEffect(() => {
    // Keep local state in sync with the global auth-disconnect signal.
    return subscribeAuthDisconnected(() => {
      setKickout(getAuthKickoutState());
    });
  }, []);
  React.useEffect(() => {
    // Also refresh kickout snapshot on navigation changes.
    setKickout(getAuthKickoutState());
  }, [location.pathname, location.search, location.hash]);

  // If we were already in the app, don't blank it out during background refresh.
  if (loading && hadUserRef.current) return <Outlet />;
  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-black text-neutral-400">
        <div className="animate-pulse">Loading…</div>
      </div>
    );
  }

  if (isVisitorUser(user)) {
    return <Navigate to="/" replace />;
  }

  if (user) return <Outlet />;

  // If the app explicitly kicked us out while we were on a protected page,
  // keep the current UI mounted and show a login overlay.
  if (kickout) {
    return (
      <>
        <Outlet />
        <Login inline from={kickout.from} />
      </>
    );
  }

  // First-time unauthenticated access to a protected route: send users to
  // the public visitor preview and let them sign in from there.
  return <Navigate to={{ pathname: '/', search: location.search, hash: location.hash }} replace />;
}
